/**
 * AI Assist Settings Store — workspace-scoped toggles for non-agent LLM features.
 */

import { create } from 'zustand';
import {
  listAiAssistSettings,
  upsertAiAssistSetting,
} from '../../lib/aiAssistSettingsService';
import {
  hydrateAssistSettingsCache,
  type AssistSettingsMap,
} from '../../lib/ai/aiAssistSettings';

interface WorkspaceSlice {
  settings: AssistSettingsMap;
  isLoading: boolean;
  error: string | null;
  loadedWorkspaceId: string | null;
  version: number;
}

function emptySlice(): WorkspaceSlice {
  return {
    settings: {},
    isLoading: false,
    error: null,
    loadedWorkspaceId: null,
    version: 0,
  };
}

interface AiAssistSettingsStoreState {
  byWorkspace: Record<string, WorkspaceSlice>;
  savingCapabilityId: string | null;

  fetchAll: (workspaceId: string) => Promise<void>;
  saveEnabled: (
    workspaceId: string,
    capabilityId: string,
    enabled: boolean,
    updatedBy?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  getSettings: (workspaceId: string) => AssistSettingsMap;
  getVersion: (workspaceId: string) => number;
}

export const useAiAssistSettingsStore = create<AiAssistSettingsStoreState>((set, get) => ({
  byWorkspace: {},
  savingCapabilityId: null,

  fetchAll: async (workspaceId) => {
    const existing = get().byWorkspace[workspaceId];
    if (existing?.loadedWorkspaceId === workspaceId && !existing.isLoading) {
      hydrateAssistSettingsCache(workspaceId, existing.settings);
      return;
    }

    set((s) => ({
      byWorkspace: {
        ...s.byWorkspace,
        [workspaceId]: { ...(s.byWorkspace[workspaceId] ?? emptySlice()), isLoading: true, error: null },
      },
    }));

    const { data, error } = await listAiAssistSettings(workspaceId);
    const settings: AssistSettingsMap = {};
    for (const row of data) {
      settings[row.capabilityId] = row.enabled;
    }

    hydrateAssistSettingsCache(workspaceId, settings);

    set((s) => ({
      byWorkspace: {
        ...s.byWorkspace,
        [workspaceId]: {
          settings,
          isLoading: false,
          error,
          loadedWorkspaceId: workspaceId,
          version: (s.byWorkspace[workspaceId]?.version ?? 0) + 1,
        },
      },
    }));
  },

  saveEnabled: async (workspaceId, capabilityId, enabled, updatedBy) => {
    set({ savingCapabilityId: capabilityId });
    const { error } = await upsertAiAssistSetting({
      workspaceId,
      capabilityId,
      enabled,
      updatedBy,
    });

    if (error) {
      set({ savingCapabilityId: null });
      return { success: false, error };
    }

    set((s) => {
      const prev = s.byWorkspace[workspaceId] ?? emptySlice();
      const settings = { ...prev.settings, [capabilityId]: enabled };
      hydrateAssistSettingsCache(workspaceId, settings);
      return {
        byWorkspace: {
          ...s.byWorkspace,
          [workspaceId]: {
            ...prev,
            settings,
            loadedWorkspaceId: workspaceId,
            version: prev.version + 1,
          },
        },
      };
    });

    set({ savingCapabilityId: null });
    return { success: true };
  },

  getSettings: (workspaceId) => get().byWorkspace[workspaceId]?.settings ?? {},

  getVersion: (workspaceId) => get().byWorkspace[workspaceId]?.version ?? 0,
}));
